import { Button, CircularProgress, Dialog, DialogContent, DialogTitle, Grid, List, ListItem, ListItemText, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { Api } from "../../api/Api";
import { SegmentDto } from "../../store/models/segments/SegmentDto";
import { SegmentLinkedBonusRuleDto, SegmentLinkedCampaignDto } from "../../store/models/segments/SegmentReferencesDto";
import NotificationList from "../notification/NotificationList";

interface SegmentDeleteDialogProps {
  segment: SegmentDto;
  setConfirmVisible: (confirm: boolean) => void;
  onConfirm: () => void;
}

const SegmentDeleteDialog = (props: SegmentDeleteDialogProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [linkedBonusRules, setLinkedBonusRules] = useState<SegmentLinkedBonusRuleDto[]>([]);
  const [linkedCampaigns, setLinkedCampaigns] = useState<SegmentLinkedCampaignDto[]>([]);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const fetchReferences = async () => {
      try {
        const [bonusRules, campaigns] = await Promise.all([
          Api.getSegmentLinkedBonusRules(props.segment.externalSegmentId),
          Api.getSegmentLinkedCampaigns(props.segment.externalSegmentId),
        ]);
        setLinkedBonusRules(bonusRules);
        setLinkedCampaigns(campaigns);
      } catch {
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchReferences();
  }, [props.segment.externalSegmentId]);

  const hasReferences = linkedBonusRules.length > 0 || linkedCampaigns.length > 0;

  return (
    <Dialog onClose={() => props.setConfirmVisible(false)} open={true} maxWidth="sm" fullWidth>
      <NotificationList isDialog />
      <DialogTitle>{t("segmentsPage.confirmDeleteTitle")}</DialogTitle>

      <DialogContent>
        {loading ? (
          <Grid container justifyContent="center" style={{ padding: theme.spacing(4) }}>
            <CircularProgress />
          </Grid>
        ) : loadError ? (
          <>
            <Typography style={{ marginBottom: theme.spacing(2) }}>
              {t("segmentsPage.deleteDialog.loadError")}
            </Typography>
            <Grid container justifyContent="flex-end">
              <Button
                color="secondary"
                variant="contained"
                onClick={() => props.setConfirmVisible(false)}
                style={{ margin: theme.spacing(0, 0, 2, 2) }}
              >
                {_.capitalize(t("common.cancel"))}
              </Button>
            </Grid>
          </>
        ) : (
          <>
            <Typography style={{ marginBottom: theme.spacing(2) }}>
              {t("segmentsPage.confirmDeleteDescription", { name: props.segment.name })}
            </Typography>

            {hasReferences && (
              <>
                <Typography variant="body2" color="error" style={{ marginBottom: theme.spacing(1) }}>
                  {t("segmentsPage.deleteDialog.warningMessage")}
                </Typography>

                {linkedBonusRules.length > 0 && (
                  <div style={{ marginBottom: theme.spacing(2) }}>
                    <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
                      {t("segmentsPage.deleteDialog.linkedBonusRules", { count: linkedBonusRules.length })}
                    </Typography>
                    <List dense disablePadding>
                      {linkedBonusRules.map((rule) => (
                        <ListItem key={rule.ruleNumber} disableGutters style={{ paddingLeft: theme.spacing(2) }}>
                          <ListItemText
                            primary={rule.ruleName}
                            secondary={`${rule.ruleNumber} · ${rule.status}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </div>
                )}

                {linkedCampaigns.length > 0 && (
                  <div style={{ marginBottom: theme.spacing(2) }}>
                    <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
                      {t("segmentsPage.deleteDialog.linkedCampaigns", { count: linkedCampaigns.length })}
                    </Typography>
                    <List dense disablePadding>
                      {linkedCampaigns.map((campaign) => (
                        <ListItem key={campaign.campaignNumber} disableGutters style={{ paddingLeft: theme.spacing(2) }}>
                          <ListItemText
                            primary={campaign.campaignName}
                            secondary={`${campaign.campaignNumber} · ${campaign.status}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </div>
                )}

                <Typography variant="body2" style={{ marginBottom: theme.spacing(2), fontStyle: "italic" }}>
                  {t("segmentsPage.deleteDialog.impactNote")}
                </Typography>
              </>
            )}

            <Grid container justifyContent="flex-end">
              <Button
                color="secondary"
                variant="contained"
                onClick={() => props.setConfirmVisible(false)}
                style={{ margin: theme.spacing(0, 0, 2, 2) }}
              >
                {_.capitalize(t("common.cancel"))}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  props.onConfirm();
                  props.setConfirmVisible(false);
                }}
                style={{ margin: theme.spacing(0, 0, 2, 2) }}
              >
                {_.capitalize(t("common.delete"))}
              </Button>
            </Grid>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SegmentDeleteDialog;
